import { computed, type Ref } from 'vue'
import { ITicketCartPayload } from '@/features/tickets/types'

// Constants for pricing calculations
export const PRICING_CONSTANTS = {
  PLATFORM_FEE_RATE: 0.07, // 7%
  TAX_RATE: 0.1, // 10%
} as const

export interface Ticket {
  id: number
  title: string
  price: number
  priceWithDiscount?: number
}

export const usePricing = (tickets: Ref<Ticket[] | undefined>, cart: Ref<ITicketCartPayload>) => {
  const getTicketById = (ticketId: number): Ticket | undefined => {
    return tickets.value?.find((ticket) => ticket.id === ticketId)
  }

  const getTicketPrice = (ticketId: number): number => {
    const ticket = getTicketById(ticketId)
    if (!ticket) return 0
    return ticket.priceWithDiscount ?? ticket.price
  }

  const getTicketOriginalPrice = (ticketId: number): number => {
    const ticket = getTicketById(ticketId)
    return ticket?.price ?? 0
  }

  const getTicketPriceByQuantity = (ticketId: number, quantity: number): number => {
    return getTicketPrice(ticketId) * quantity
  }

  const subtotal = computed(() => {
    return cart.value.tiers.reduce((total, tier) => {
      return total + getTicketPrice(tier.tierId) * tier.quantity
    }, 0)
  })

  const originalSubtotal = computed(() => {
    return cart.value.tiers.reduce((total, tier) => {
      return total + getTicketOriginalPrice(tier.tierId) * tier.quantity
    }, 0)
  })

  const platformFees = computed(() => {
    return originalSubtotal.value * PRICING_CONSTANTS.PLATFORM_FEE_RATE
  })

  const taxes = computed(() => {
    return originalSubtotal.value * PRICING_CONSTANTS.TAX_RATE
  })

  const totalAmount = computed(() => {
    return subtotal.value + platformFees.value + taxes.value
  })

  const toCurrency = (amount: number): string => {
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  }

  return {
    getTicketById,
    getTicketPrice,
    getTicketPriceByQuantity,
    subtotal,
    platformFees,
    taxes,
    totalAmount,
    toCurrency,
    PRICING_CONSTANTS,
  }
}
